import { Component, inject } from "@angular/core";
import { ROUTES } from "../../constants/routes";
import { RouterLink } from "@angular/router";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Footer } from "../../components/footer/footer";
import { Navbar } from "../../components/navbar/navbar";
import { SeoService } from "../../services/seo.service";
import { SITE } from "../../constants/site";
import { ICONS } from "../../constants/icons";
import {
  CUSTOMER_SEGMENTS,
  PROOF_STATS,
  TRUST_SIGNALS
} from "../../constants/social-proof";
import { DATA_SOURCES } from "../../constants/data-sources";
import { ATTESTATIONS, CONTROL_AREAS } from "../../constants/compliance";
import {
  faqSchema,
  organizationSchema,
  serviceSchema,
  softwareApplicationSchema,
  webPageSchema,
  websiteSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-home-page",
  imports: [RouterLink, Navbar, Footer],
  templateUrl: "./home-page.html",
  styleUrl: "./home-page.scss"
})
export class HomePage {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly site = SITE;

  // Social proof, sourcing and compliance content live in shared constants so the
  // same wording is reused on the interior pages and never drifts out of sync.
  readonly customerSegments = CUSTOMER_SEGMENTS;
  readonly proofStats = PROOF_STATS;
  readonly trustSignals = TRUST_SIGNALS;
  readonly dataSources = DATA_SOURCES;
  readonly attestations = ATTESTATIONS;
  readonly controlAreas = CONTROL_AREAS;

  readonly apiSample = `{
  "first_name": "Jane",
  "last_name": "Doe",
  "state": "TX"
}

// response
{
  "deceased": true,
  "date_of_death": "2025-09-21",
  "age_at_death": 77,
  "confidence": 0.98
}`;

  iconFor(name: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(ICONS[name] ?? "");
  }

  matchFields = [
    { label: "Full name", value: "Jane A. Doe" },
    { label: "Date of birth", value: "04 / 12 / 1948" },
    { label: "Date of death", value: "21 / 09 / 2025" },
    { label: "Age at death", value: "77" },
    { label: "Property address", value: "4207 Oak Ridge Dr, Austin, TX" }
  ];

  dataPoints = [
    {
      title: "Date of birth and date of death",
      description:
        "Match your records to confirmed deceased consumers with a precise date of birth and date of death.",
      icon: "calendar"
    },
    {
      title: "Age at death",
      description:
        "Age is calculated for you so your team can validate and segment records at a glance.",
      icon: "user"
    },
    {
      title: "Last known property address",
      description:
        "Every match is enriched with the last known property and mailing address on file.",
      icon: "home"
    },
    {
      title: "City and state location",
      description:
        "Nationwide city and state coverage keeps your compliance and identity checks accurate.",
      icon: "pin"
    }
  ];

  steps = [
    {
      title: "We refresh the data every week",
      description:
        "Our team publishes fresh confirmed deceased consumer records every week, often within seven days of passing."
    },
    {
      title: "You upload your list",
      description:
        "Send a file with first name, middle name, last name and any details you have. CSV or Excel, no size limit."
    },
    {
      title: "We match against the database",
      description:
        "Our matching engine compares your records against millions of confirmed deceased records nationwide."
    },
    {
      title: "You get verified results back",
      description:
        "Download matches with date of death, age and property address. Pay only for the records we confirm."
    }
  ];

  industries = [
    {
      title: "Banks and credit unions",
      description: "Stop deceased account takeover and new account fraud before funds move.",
      link: ROUTES.solutionsBanks.url
    },
    {
      title: "Lenders and card issuers",
      description: "Screen every applicant against confirmed deceased identities.",
      link: ROUTES.solutionsLenders.url
    },
    {
      title: "Employers and HR",
      description: "Catch deceased identity use in hiring, onboarding and payroll.",
      link: ROUTES.solutionsEmployers.url
    },
    {
      title: "Insurance carriers",
      description: "Confirm date of death for claims, policy reviews and audits.",
      link: ROUTES.solutionsInsurance.url
    },
    {
      title: "Government agencies",
      description: "Prevent benefit and entitlement payments to deceased recipients.",
      link: ROUTES.solutionsGovernment.url
    },
    {
      title: "Collections and recovery",
      description: "Suppress deceased accounts before your team makes contact.",
      link: ROUTES.solutionsCollections.url
    }
  ];

  faqs = [
    {
      question: "What is VerifyDeceased?",
      answer:
        "VerifyDeceased is a deceased consumer verification service that lets banks, lenders and enterprises confirm whether a person is deceased. You upload a list of names and receive confirmed matches with date of birth, date of death, age and last known property address."
    },
    {
      question: "What exactly is free when I create an account?",
      answer:
        "Creating an account is free and no credit card is required. There is no subscription, no setup fee, no minimum and no contract. You add funds only when you are ready to run verifications, and you are charged $0.04 per confirmed match. Records we cannot confirm as deceased are never billed, so a test list only costs you for the deaths we actually verify."
    },
    {
      question: "Where does your death data come from?",
      answer:
        "We combine state and county vital records, the Social Security Administration Death Master File accessed through the NTIS certification program, published obituaries and funeral home notices, cemetery and interment records, and probate and estate court filings. Confirmed records are then enriched with last known property and mailing address data from our parent company LeadCruncher."
    },
    {
      question: "How fresh is your death data?",
      answer:
        "We add confirmed deceased consumer records every week, often within seven days of a person passing away. Most other providers are two to three months behind, which leaves a window fraudsters use to open accounts and apply for credit in a deceased person's name."
    },
    {
      question: "Are you SOC 2 certified and do you support GLBA?",
      answer:
        "We are working toward a SOC 2 Type II report and we do not claim one until it is complete. Our information security program is built and documented against the FTC Safeguards Rule at 16 CFR Part 314, with a designated Qualified Individual, a written risk assessment, multi factor authentication, AES-256 encryption at rest, TLS 1.2 or higher in transit, logging and monitoring, and a documented incident response plan. The full control mapping is published on our security page."
    },
    {
      question: "How much does it cost to verify records?",
      answer:
        "Standard verification is $0.04 per record. Bulk pricing drops to $0.01 per record when you verify at least 100,000 records per month. You only pay for confirmed matches, so there is no charge for records we cannot verify."
    },
    {
      question: "What fields do I need to upload?",
      answer:
        "At a minimum you need first name and last name. Adding middle name, date of birth, city or state improves match accuracy. You can upload a CSV or Excel file, or send records in real time through our API."
    },
    {
      question: "Do you offer an API for real time verification?",
      answer:
        "Yes. Our REST API lets banks, card issuers and platforms verify a single identity or a batch in real time during account opening, credit applications or claims processing. See the developers page for full API documentation."
    },
    {
      question: "Which areas do you cover?",
      answer:
        "VerifyDeceased provides confirmed deceased consumer records across all 50 states and the District of Columbia, with nationwide city and state coverage."
    }
  ];

  ngOnInit(): void {
    this.seo.apply({
      title: "VerifyDeceased | Verify Deceased Consumers, Stop Identity Fraud",
      description: this.site.metaDescription,
      path: "/",
      keywords:
        "deceased verification, death verification, verify deceased consumer, deceased identity fraud, death data, deceased suppression, death audit, deceased screening API",
      structuredData: [
        organizationSchema(),
        websiteSchema(),
        webPageSchema({
          name: "VerifyDeceased | Verify Deceased Consumers, Stop Identity Fraud",
          description: this.site.metaDescription,
          path: "/"
        }),
        serviceSchema(),
        softwareApplicationSchema(),
        faqSchema(this.faqs)
      ]
    });
  }
}
